import { BeakerIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../catalyst/button';
import AuthGuard from '../../components/AuthGuard';
import { useKeyPressed } from '../../hooks/useKeyPressed';
import { useAuth } from '../../hooks/useAuth';
import type { CategoryActions } from './useCategoryActions';

export function Header({
  actions,
  trainingEnabled,
  onDelete,
}: {
  actions: CategoryActions;
  trainingEnabled: boolean;
  // Absent until the category has loaded — the confirmation dialog names the category,
  // so there is nothing to confirm before then.
  onDelete?: () => void;
}) {
  const altPressed = useKeyPressed('Alt');
  const busy = actions.pendingAction !== null;
  // The Alt-revealed PDF parser reset stays admin-only: it re-drives every page in the
  // category, while Reclassify / Train are the curator's everyday levers.
  const isAdmin = useAuth().hasAccess('admin');
  const showRebuild = isAdmin && (altPressed || actions.pendingAction === 'rebuild');

  return (
    <PageHeader
      title="Category View"
      description="Manage your document classification category"
      icon={<BeakerIcon className="size-6 " />}
      showBackButton={true}
    >
      <div className="flex items-center gap-2 whitespace-nowrap">
        <AuthGuard roles={['admin', 'curator']}>
          {showRebuild ? (
            <Button onClick={actions.rebuild} disabled={busy} color="red">
              {actions.pendingAction === 'rebuild' ? actions.pendingLabel : 'Reset PDF Parser for all pages'}
            </Button>
          ) : (
            <Button onClick={actions.reclassify} disabled={busy || !trainingEnabled} outline>
              {actions.pendingAction === 'reclassify' ? actions.pendingLabel : 'Reclassify Category'}
            </Button>
          )}
          <Button onClick={actions.train} disabled={busy || !trainingEnabled} outline>
            {actions.pendingAction === 'train' ? actions.pendingLabel : 'Train Category'}
          </Button>
        </AuthGuard>
        <AuthGuard roles={['admin']}>
          {/* Destructive, so it carries the same red trash marking the Document actions
              menu uses — outline rather than a solid red button, which would shout on
              every category page. */}
          {onDelete && (
            <Button onClick={onDelete} disabled={busy} outline>
              <TrashIcon className="text-red-600 dark:text-red-400" />
              <span className="text-red-600 dark:text-red-400">Delete Category</span>
            </Button>
          )}
        </AuthGuard>
      </div>
    </PageHeader>
  );
}
